/**
 * The three recovery actions an operator can take on one record by hand.
 *
 * This screen used to be a single form with a dropdown of verbs, a
 * "target type" box and a "target ID" box. That shape was left over from when
 * the list was eight verbs long and it hid the only thing that matters here:
 * the surviving controls have nothing in common. Each one acts on a
 * different table, and `target_id` means something different for every one of
 * them - a member for `pause_user`, a queued message for `retry_notification`
 * and a recorded event for `replay_event`. One dropdown asking for "a target ID" cannot say that, so an
 * operator had to already know it.
 *
 * So each control gets its own card stating what it acts on, what it changes,
 * and what can be undone, and asks only for the field it actually uses.
 *
 * Two things the API does not do, which this screen has to work around rather
 * than pretend about:
 *
 *   - `target_type` and `parameters` are recorded against the audit row but no
 *     control reads either of them. They were free-text inputs before, which
 *     invited an operator to type something meaningful into a field with no
 *     effect. Each control now sends its own fixed `target_type` so the audit
 *     log still says what was acted on, and nothing asks for `parameters`.
 *   - a malformed id reaches Postgres as a failed `CAST(... AS uuid)` and comes
 *     back as a 500 with no explanation, so the id is shape-checked here first.
 *
 * Nothing reads issued controls back: `operational_controls` rows are only ever
 * surfaced for `target_type='task'`, which none of these write. The
 * receipt list at the bottom is therefore this browser session's own record and
 * says so.
 */

import { useState } from 'react';
import { controlApiV1InternalControlsPost } from '@closet-theory/api';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, Code, Section, StatusBadge } from '@ui/console/primitives';
import { Term } from '@ui/console/glossary';
import { IdentifierValue } from '@ui/console/measures';
import { RecordTable } from './record-table';
import { CopyableId, EntityRef, IdentifierCopy, MemberPicker } from './references';
import { TextAreaField, TextField } from '@ui/console/forms';
import type { Tone } from '@ui/console/status';
import { handleUnauthorized, configureAdminApi, currentRole } from './auth';
import { Actions, Page } from './components';
import { errorDetail } from './operation-policy';

export type ControlVerb = 'replay_event' | 'pause_user' | 'retry_notification';

const CONTROL_REASON: Record<ControlVerb, string> = {
  replay_event: 'Replayed an event from the operator controls console.',
  pause_user: 'Paused a member from the operator controls console.',
  retry_notification: 'Retried a notification from the operator controls console.',
};

export type ControlDefinition = {
  control: ControlVerb;
  /** Plain words for what this does; the verb itself stays on screen beside it. */
  title: string;
  /** Sent as `target_type`. Recorded in the audit log; no control branches on it. */
  targetType: string;
  /** What the id is, or `undefined` when the control takes no target. */
  target?: {
    label: string;
    hint: string;
    placeholder: string;
    /** Chosen by name from a search rather than pasted, when the target is a member. */
    picker?: 'member';
  };
  /** Exactly what changes, in the order it changes. */
  effect: readonly string[];
  /** What can be put back, and how. An operator reads this before confirming. */
  reversal: string;
  /** When you would reach for this one. */
  when: string;
  tone: Tone;
  /** Shown as a consequence the operator must accept before the call is made. */
  consequence?: string;
  /** Roles narrower than the screen's own gate. */
  requiresSuperAdmin?: boolean;
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const CONTROLS: readonly ControlDefinition[] = [
  {
    control: 'pause_user',
    title: 'Pause a member and sign them out',
    targetType: 'user',
    target: {
      label: 'Member',
      hint: 'Search by the member’s name or email. A pasted member id also works.',
      placeholder: '00000000-0000-0000-0000-000000000000',
      picker: 'member',
    },
    effect: [
      'Sets the member’s account status to paused, so the product stops serving them.',
      'Disables their sign-in credential, so a new sign-in is refused.',
      'Revokes every refresh session they hold, so devices already signed in are signed out at once.',
    ],
    reversal:
      'Nothing here reverses it. Restoring the account, re-enabling the credential and letting them sign in again is a separate action, and the member has to sign in on every device afterwards.',
    when: 'A member is abusing the product or has asked to be locked out immediately.',
    tone: 'error',
    consequence:
      'This signs the member out of every device they are on right now, and there is no undo button on this screen.',
  },
  {
    control: 'retry_notification',
    title: 'Queue a failed message for another delivery attempt',
    targetType: 'notification_delivery',
    target: {
      label: 'Delivery ID',
      hint: 'The id of the delivery row that failed, not the member and not the message template.',
      placeholder: '00000000-0000-0000-0000-000000000000',
    },
    effect: [
      'Puts the delivery back into the queued state and clears its error code, so a delivery worker picks it up again.',
      'Only a delivery currently recorded as failed is changed. Any other state is left exactly as it is and the call still reports success.',
    ],
    reversal:
      'Safe to repeat. The member may receive the message, which cannot be recalled once it is sent.',
    when: 'A member says they never received a message and its delivery is recorded as failed.',
    tone: 'info',
  },
  {
    control: 'replay_event',
    title: 'Send a recorded event through delivery again',
    targetType: 'domain_event',
    target: {
      label: 'Event ID',
      hint: 'The id of the recorded event to re-deliver, from the audit log.',
      placeholder: '00000000-0000-0000-0000-000000000000',
    },
    effect: [
      'Copies the recorded event into the delivery queue as a new entry, so everything that reacts to that event runs again.',
      'The original event is not modified; a second copy is queued beside it.',
    ],
    reversal:
      'Cannot be cancelled once queued. Anything that reacted to the event the first time reacts again, so a side effect that is not safe to repeat happens twice.',
    when: 'Something downstream missed an event and needs it delivered a second time.',
    tone: 'warning',
    consequence:
      'Every downstream reaction to this event runs a second time. If any of them sends a message or charges something, it happens again.',
    requiresSuperAdmin: true,
  },
];

export function definitionFor(control: ControlVerb): ControlDefinition | undefined {
  return CONTROLS.find((entry) => entry.control === control);
}

/** Why this id cannot be sent, or `undefined` when it can. */
export function idProblem(
  definition: ControlDefinition,
  value: string,
): { title: string; body: string } | undefined {
  if (!definition.target) return undefined;
  const trimmed = value.trim();
  if (!trimmed) {
    return {
      title: `A ${definition.target.label.toLowerCase()} is required.`,
      body: 'This control acts on one record, so it needs to be told which one.',
    };
  }
  if (!UUID.test(trimmed)) {
    return {
      title: 'That is not the shape of an id.',
      body: 'An id is 36 characters: eight, four, four, four then twelve hexadecimal digits separated by dashes. The API turns anything else into an unexplained server error rather than a refusal, so it is checked here first.',
    };
  }
  return undefined;
}

export function reasonProblem(value: string): { title: string; body: string } | undefined {
  if (value.trim().length >= 3) return undefined;
  return {
    title: 'A reason of at least three characters is required.',
    body: 'The reason is written to the audit log beside your operator account and is the only record of why this was done.',
  };
}

export type Receipt = {
  control_id: string;
  control: ControlVerb;
  target_type: string;
  target_id: string;
  status: string;
  reason: string;
  issued_at: string;
};

function ControlCard({
  definition,
  disabled,
  onIssued,
}: {
  definition: ControlDefinition;
  disabled: boolean;
  onIssued: (receipt: Receipt) => void;
}) {
  const [targetId, setTargetId] = useState('');
  const [reason, setReason] = useState(CONTROL_REASON[definition.control]);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<{ title: string; body: string }>();
  const [done, setDone] = useState<{ id: string; status: string }>();

  const validate = () => idProblem(definition, targetId);

  const issue = async () => {
    setBusy(true);
    setDone(undefined);
    configureAdminApi();
    const id = targetId.trim();
    const result = await controlApiV1InternalControlsPost({
      body: {
        control: definition.control,
        target_type: definition.targetType,
        target_id: id,
        reason: reason.trim() || CONTROL_REASON[definition.control],
        parameters: {},
      },
    });
    setBusy(false);
    setConfirming(false);
    handleUnauthorized(result.response.status);
    if (result.error || !result.data) {
      setProblem({
        title: 'The control was not accepted.',
        body: errorDetail(result.error, 'The API refused it and gave no detail.'),
      });
      return;
    }
    setProblem(undefined);
    setTargetId('');
    setReason(CONTROL_REASON[definition.control]);
    setDone({ id: result.data.control_id, status: result.data.status });
    onIssued({
      control_id: result.data.control_id,
      control: definition.control,
      target_type: definition.targetType,
      target_id: id,
      status: result.data.status,
      reason: reason.trim() || CONTROL_REASON[definition.control],
      issued_at: new Date().toISOString(),
    });
  };

  const blocked = disabled && Boolean(definition.requiresSuperAdmin);

  return (
    <section
      className="flex min-w-0 flex-col gap-3 rounded-xl border border-secondary bg-primary p-4"
      aria-labelledby={`control-${definition.control}`}
    >
      <header className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 id={`control-${definition.control}`} className="text-md font-semibold text-primary">
            {definition.title}
          </h3>
          <StatusBadge value={definition.control} />
        </div>
        <p className="text-sm text-tertiary">{definition.when}</p>
      </header>

      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-tertiary uppercase">What it changes</span>
        <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-secondary">
          {definition.effect.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>

      <p className="text-sm text-tertiary">
        <span className="font-medium text-secondary">Reversing it: </span>
        {definition.reversal}
      </p>

      {definition.requiresSuperAdmin ? (
        <p className="text-xs text-tertiary">
          Only a super admin may issue this one; the API refuses it for every other role.
        </p>
      ) : null}

      {blocked ? (
        <Callout tone="neutral" title="Your role cannot issue this control.">
          This is a permission boundary, not a fault. Ask a super admin to run it.
        </Callout>
      ) : (
        <>
          {definition.target?.picker === 'member' ? (
            <MemberPicker
              label={definition.target.label}
              value={targetId}
              hint={definition.target.hint}
              onChange={setTargetId}
            />
          ) : definition.target ? (
            <TextField
              label={definition.target.label}
              value={targetId}
              hint={definition.target.hint}
              placeholder={definition.target.placeholder}
              onChange={(event) => setTargetId(event.currentTarget.value)}
            />
          ) : (
            <p className="rounded-lg border border-secondary bg-secondary px-3 py-2 text-sm text-tertiary">
              This control takes no target. It always syncs the whole Broadway catalogue.
            </p>
          )}

          <TextAreaField
            label="Reason"
            value={reason}
            rows={2}
            hint="Written to the audit log against your operator account. It is the only record of why."
            onChange={(event) => setReason(event.currentTarget.value)}
          />

          {problem ? (
            <Callout tone="error" title={problem.title}>
              {problem.body}
            </Callout>
          ) : null}
          {done ? (
            <Callout tone="success" title="Accepted.">
              <span className="flex flex-col items-start gap-2">
                <span>
                  The control was accepted with status “{done.status}” and written to the audit log.
                  Nothing on the server can read this control back afterwards, so its id is listed
                  below for as long as this page is open.
                </span>
                <IdentifierCopy label="Control" value={done.id} />
              </span>
            </Callout>
          ) : null}

          {confirming ? (
            <Callout
              tone={definition.tone === 'info' ? 'warning' : definition.tone}
              title="Confirm before this runs"
              actions={
                <>
                  <Button
                    size="sm"
                    color="primary-destructive"
                    isDisabled={busy}
                    onClick={() => void issue()}
                  >
                    {busy ? 'Issuing…' : `Yes, ${definition.title.toLowerCase()}`}
                  </Button>
                  <Button size="sm" color="secondary" onClick={() => setConfirming(false)}>
                    Cancel
                  </Button>
                </>
              }
            >
              {definition.consequence ?? definition.reversal}
            </Callout>
          ) : (
            <Actions>
              <Button
                size="sm"
                color={definition.tone === 'error' ? 'primary-destructive' : 'primary'}
                isDisabled={busy}
                onClick={() => {
                  const found = validate();
                  setProblem(found);
                  setDone(undefined);
                  if (!found) setConfirming(true);
                }}
              >
                {definition.title}
              </Button>
            </Actions>
          )}
        </>
      )}
    </section>
  );
}

export function OperatorControls() {
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const role = currentRole();
  const superAdmin = role === 'super_admin';

  return (
    <Page
      title="Operator controls"
      description="Use this page to take one of three recovery actions on a single record by hand, when something has gone wrong and the product cannot put itself right. Every one is written to the audit log with your reason attached."
    >
      <Callout tone="warning" title="Read what a control does before you issue it">
        These three act on three different things and are not interchangeable. The id each one wants
        is the id of the record named on its own card &mdash; a member id given to{' '}
        <Code>retry_notification</Code> matches no delivery and changes nothing, while still
        reporting success. Retrying and cancelling a pipeline run are not here: those live on
        Workflow jobs, which acts on the queue the work really runs on.
      </Callout>

      <Section
        title="Controls"
        description="Each card asks only for what its own control uses. Nothing here accepts free-form parameters, because no control reads any."
      >
        <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-2">
          {CONTROLS.map((definition) => (
            <ControlCard
              key={definition.control}
              definition={definition}
              disabled={!superAdmin}
              onIssued={(receipt) => setReceipts((current) => [receipt, ...current])}
            />
          ))}
        </div>
      </Section>

      <Section
        title="Issued in this browser session"
        description="The API keeps no readable history for these controls, so this list exists only while this page is open and is gone when you close it. The audit log keeps the permanent record."
        actions={<StatusBadge value={`${receipts.length} issued`} />}
      >
        {receipts.length ? (
          <RecordTable
            noun="issued controls"
            records={receipts}
            columns={['issued_at', 'control', 'status', 'target_type', 'target_id', 'control_id']}
            columnLabel={(column) =>
              column === 'target_id' ? 'Acted on' : column === 'control_id' ? 'Control' : undefined
            }
            renderCell={(column, record) => {
              const value = record[column];
              if (typeof value !== 'string' || !value) return undefined;
              if (column === 'target_id')
                return record['target_type'] === 'user' ? (
                  <EntityRef kind="member" id={value} />
                ) : (
                  <CopyableId value={value} label={`${String(record['target_type'])} id`} full />
                );
              if (column === 'control_id')
                return <CopyableId value={value} label="control id" full />;
              return undefined;
            }}
          />
        ) : (
          <Callout tone="neutral" title="You have not issued a control on this page yet.">
            This is an empty list rather than a failed read. Controls issued before this page was
            opened, or by anyone else, are in the audit log and cannot be listed here.
          </Callout>
        )}
      </Section>

      <Callout tone="neutral" title="This page is a holding place, not where these belong">
        A console of three unrelated commands exists because the commands were built together, not
        because an operator ever wants them together. Each one belongs on the screen that owns the
        thing it acts on: pausing a member belongs beside that member on People, where their status
        already is; retrying a delivery belongs beside the message that failed. Reaching them from
        there means an operator has the record in front of them and never has to paste an id, which
        is the only way the wrong id stops being possible. Until they move, they are here and each
        one says what it acts on.
      </Callout>

      <Section
        title="What the audit log records"
        description="Every control writes one audit entry and one event, whether or not it matched a row."
      >
        <div className="flex flex-col gap-2 rounded-xl border border-secondary bg-primary p-4">
          <p className="text-sm text-secondary">
            The entry carries the control name, the record type and id you gave, your reason and
            your operator account. It is <Term term="audited" />, so it cannot be edited afterwards.
          </p>
          <IdentifierValue
            label="Audit action name"
            value="control.<name>, e.g. control.pause_user"
          />
          <p className="text-sm text-tertiary">
            A control that matched no row is recorded exactly the same way as one that matched, so
            the audit log is a record of what was asked for rather than of what changed.
          </p>
        </div>
      </Section>
    </Page>
  );
}
